import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { Proforma } from 'src/app/pages/sales/proforma/proforma.model';
import { OrderService } from 'src/app/services/order/order.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-proforma-ship-request',
  templateUrl: './proforma-ship-request.component.html',
  styleUrls: ['./proforma-ship-request.component.scss']
})
export class ProformaShipRequestComponent extends DestructorComponent implements OnInit {

  @Output() formSubmitted = new EventEmitter<void>();

  shipRequestForm: FormGroup;
  selectedProforma: Proforma | null = null;
  formSubmittedFlag: boolean = false;
  isLoading: boolean = false;
  submitError: string = '';

  constructor(private fb: FormBuilder, private _orderService: OrderService) {
    super();
    this.shipRequestForm = this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): FormGroup {
    return this.fb.group({
      consigneeName: ['', Validators.required],
      address: ['', Validators.required],
      emailAddress: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', Validators.required],
      detailsConfirmed: [false],
      agentNotes: ['', Validators.required]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_proforma_ship_request');
    if (modalElement) {
      modalElement.addEventListener('show.bs.modal', () => {
        const proforma = (window as any).selectedProformaForShipRequest;
        if (proforma) {
          this.selectedProforma = proforma;
          this.shipRequestForm.patchValue({
            consigneeName: proforma.buyer_consignee_name || '',
            address: proforma.buyer_street_address || '',
            emailAddress: proforma.buyer_email_address || '',
            phoneNumber: proforma.buyer_telephone_no || ''
          });
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
      });
    }
  }

  onDetailsConfirmedChange(): void {
    const agentNotes = this.shipRequestForm.get('agentNotes')!;
    if (this.shipRequestForm.get('detailsConfirmed')!.value) {
      agentNotes.clearValidators();
    } else {
      agentNotes.setValidators(Validators.required);
    }
    agentNotes.updateValueAndValidity();
  }

  isSubmitDisabled(): boolean {
    if (this.isLoading) return true;
    const { detailsConfirmed, agentNotes, consigneeName, address, emailAddress, phoneNumber } = this.shipRequestForm.controls;
    const consigneeInvalid = consigneeName.invalid || address.invalid || emailAddress.invalid || phoneNumber.invalid;
    if (consigneeInvalid) return true;
    if (!detailsConfirmed.value && !(agentNotes.value || '').trim()) return true;
    return false;
  }

  onSubmit(): void {
    this.formSubmittedFlag = true;

    if (this.isSubmitDisabled() || !this.selectedProforma) return;

    this.isLoading = true;
    this.submitError = '';

    const formValue = this.shipRequestForm.value;
    const updateData = {
      ship_requested: true,
      buyer_consignee_name: formValue.consigneeName,
      buyer_street_address: formValue.address,
      buyer_email_address: formValue.emailAddress,
      buyer_telephone_no: formValue.phoneNumber,
      shipping_confirmed_check: formValue.detailsConfirmed,
      shipping_agent_notes: formValue.agentNotes || ''
    };

    this.subscriptions.push(
      this._orderService.UpdateData(this.selectedProforma._id, updateData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            this.closeModal();
            this.formSubmitted.emit();
            Swal.fire({
              title: 'Success!',
              text: 'Shipping has been requested successfully.',
              icon: 'success',
              confirmButtonText: 'Ok, got it!',
              buttonsStyling: false,
              customClass: { confirmButton: 'btn btn-primary' }
            });
          } else {
            this.submitError = response.message || 'Failed to submit ship request. Please try again.';
          }
        },
        error: () => {
          this.isLoading = false;
          this.submitError = 'There was an error requesting shipping. Please try again.';
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_proforma_ship_request');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
  }

  private resetForm(): void {
    this.shipRequestForm.reset({ detailsConfirmed: false });
    const agentNotes = this.shipRequestForm.get('agentNotes')!;
    agentNotes.setValidators(Validators.required);
    agentNotes.updateValueAndValidity();
    this.formSubmittedFlag = false;
    this.isLoading = false;
    this.submitError = '';
    this.selectedProforma = null;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.shipRequestForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched || this.formSubmittedFlag));
  }

}
